"use client";

import { motion } from "motion/react";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useLoadErrorRecovery } from "@/hooks/use-load-error-recovery";
import {
  captureClientEvent,
  captureClientException,
} from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import {
  getLoadErrorRecoveryKind,
  getLoadErrorSignature,
} from "@/lib/load-error-recovery";
import { createClientLogger } from "@/lib/logging/client";
import { isNativeCapacitorRuntime } from "@/lib/telemetry/runtime";
import { HiiveLogoIcon } from "@/svgs/hiive-logo-icon";

const logger = createClientLogger("components/ui/app-error");

function getErrorTelemetryProperties({
  error,
  isGlobal,
  isRecoverable,
}: {
  error: Error & { digest?: string };
  isGlobal: boolean;
  isRecoverable: boolean;
}) {
  return {
    source: "app_error_boundary",
    error_name: error.name,
    error_message: error.message?.slice(0, 500),
    digest: error.digest,
    has_stack: Boolean(error.stack),
    is_global: isGlobal,
    is_recoverable: isRecoverable,
    pathname: window.location.pathname,
    href: window.location.href,
    online: navigator.onLine,
    visibility_state: document.visibilityState,
    native_capacitor: isNativeCapacitorRuntime(),
  };
}

interface AppErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
  isGlobal?: boolean;
}

export function AppError({ error, reset, isGlobal }: AppErrorProps) {
  const recoveryKind = getLoadErrorRecoveryKind(error);
  const isRecoverable = recoveryKind !== null;
  const reportedSignatureRef = useRef<string | null>(null);

  useLoadErrorRecovery({ error, recoveryKind });

  useEffect(() => {
    const signature = [
      isGlobal ? "global" : "route",
      isRecoverable ? "recoverable" : "fatal",
      getLoadErrorSignature(error),
      error.stack?.slice(0, 160),
    ]
      .filter(Boolean)
      .join(":");
    if (reportedSignatureRef.current === signature) return;
    reportedSignatureRef.current = signature;

    const properties = getErrorTelemetryProperties({
      error,
      isGlobal: Boolean(isGlobal),
      isRecoverable,
    });

    logger.error(error);
    captureClientException(error, properties);
    captureClientEvent(
      isGlobal ? ANALYTICS_EVENTS.GLOBAL_APP_ERROR : ANALYTICS_EVENTS.APP_ERROR,
      properties,
    );
  }, [error, isGlobal, isRecoverable]);

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col items-center justify-center overflow-hidden bg-background p-6 text-center font-sans text-foreground">
      {/* Background - Clean & Premium */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 40,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
          className="absolute -top-[50%] -left-[50%] h-[200vh] w-[200vh] rounded-full bg-[radial-gradient(circle_at_center,rgba(var(--primary),0.03)_0%,transparent_50%)]"
        />
      </div>

      <div className="relative z-10 flex w-full max-w-lg flex-col items-center">
        {/* Logo Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative mb-12"
        >
          {/* Floating Logo - Transitioning between Yellow (Brand) and Red */}
          <motion.div
            animate={{
              y: [-8, 8, -8],
              color: ["#daa520", "#ef4444", "#daa520"], // Gold -> Red -> Gold
            }}
            transition={{
              y: {
                duration: 6,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              },
              color: {
                duration: 4,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
              },
            }}
            className="relative z-10 drop-shadow-2xl"
          >
            <HiiveLogoIcon width={160} height={160} color="currentColor" />
          </motion.div>
        </motion.div>

        {/* Text Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="space-y-4 px-4"
        >
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {isRecoverable
              ? "Loading latest version"
              : isGlobal
                ? "App needs a restart"
                : "This page could not load"}
          </h2>
          <p className="mx-auto max-w-sm text-base leading-relaxed text-muted-foreground">
            {isRecoverable
              ? "We found a stale app file and are reloading a clean copy."
              : "Something went wrong. The error has been logged."}
          </p>
        </motion.div>

        {/* Actions - Full Width & Clean */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-12 flex w-full max-w-sm flex-col gap-3"
        >
          <Button
            onClick={() => {
              if (isRecoverable) {
                window.location.reload();
                return;
              }
              reset();
            }}
            size="lg"
            className="w-full rounded-full text-base font-medium shadow-sm transition-all hover:shadow-md"
          >
            {isRecoverable ? "Reload now" : "Try again"}
          </Button>

          <Button
            variant="outline"
            size="lg"
            className="w-full rounded-full border-border transition-all hover:bg-surface-alt hover:text-foreground"
            onClick={() => {
              const fallback = window.location.pathname.startsWith("/chat/")
                ? "/chat"
                : "/";
              if (window.history.length > 1) {
                window.history.back();
                return;
              }
              window.location.href = fallback;
            }}
          >
            Go back
          </Button>

          <Button
            variant="ghost"
            size="lg"
            className="w-full rounded-full transition-all hover:bg-surface-alt hover:text-foreground"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Return Home
          </Button>
        </motion.div>
      </div>

      {/* Footer Details - Minimalist */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 flex items-center gap-2 font-mono text-[10px] tracking-widest text-muted-foreground/30 uppercase"
      >
        <span>Error</span>
        <span className="h-px w-3 bg-border/50" />
        <span>{error.digest?.slice(0, 8) || "UNKNOWN"}</span>
      </motion.div>
    </div>
  );
}
